// cSpell:ignore Silae cumul mtpart annee Affecte Ajoute
import type { CounterAndStockageValueType } from "@/types/silae";
import type {
  FormatterContext,
  FormattedBloc52,
} from "@/types/silae-formatters";

import { formatSilaeDateExpression } from "./sources";

const COUNTER_TYPE_I18N_KEYS: Record<string, string> = {
  AjouteCPAcquisAnt: "counterFeedingConfig.counterTypes.cpAcquisAnt",
  AjouteCPPrisAnt: "counterFeedingConfig.counterTypes.cpPrisAnt",
  AjouteCpAcquisRef: "counterFeedingConfig.counterTypes.cpAcquisRef",
  AjouteCPPrisRef: "counterFeedingConfig.counterTypes.cpPrisRef",
  AffecteCPSupJoursAcquis: "counterFeedingConfig.counterTypes.cpSupAcquis",
  AffecteCPSupJoursPris: "counterFeedingConfig.counterTypes.cpSupPris",
  AffecteCPSup2JoursAcquis: "counterFeedingConfig.counterTypes.cpSup2Acquis",
  AffecteCPSup2JoursPris: "counterFeedingConfig.counterTypes.cpSup2Pris",
  AjouteRTTAcquis: "counterFeedingConfig.counterTypes.rttAcquis",
  AjouteRTTpris: "counterFeedingConfig.counterTypes.rttPris",
};

export function formatBonusCounterType(
  code: string | undefined,
  ctx: FormatterContext,
): string {
  if (!code) return "";
  const key = COUNTER_TYPE_I18N_KEYS[code];
  if (!key) return code;
  return ctx.t(key, code);
}

const VALUE_TYPE_FALLBACKS: Record<CounterAndStockageValueType, string> = {
  bases: "Résultat de Bases",
  tauxs: "Résultat de Taux",
  bases_times_tauxs: "Bases × Taux",
  custom: "Expression personnalisée",
};

export function formatBonusValueType(
  type: CounterAndStockageValueType | undefined,
  ctx: FormatterContext,
): string {
  if (!type) return "";
  const fallback = VALUE_TYPE_FALLBACKS[type] ?? type;
  return ctx.t(`step5Validation.formatters.valueTypes.${type}`, fallback);
}

const BLOC52_FALLBACKS: Record<string, string> = {
  "026": "Prime exceptionnelle liée à l'activité",
  "027": "Prime liée à l'activité",
  "028": "Prime non liée à l'activité",
  "029": "Repos compensateur ou rachat de RTT",
  "030": "Rachat CET",
};

export function formatBonusBloc52(
  code: string | undefined,
  customCode: string | undefined,
  ctx: FormatterContext,
): FormattedBloc52 | null {
  if (!code) return null;
  if (code === "custom") {
    const pill = (customCode ?? "").trim() || "—";
    return {
      pill,
      label: ctx.t(
        "step5Validation.formatters.bloc52.custom",
        "Code personnalisé",
      ),
    };
  }
  const fallback = BLOC52_FALLBACKS[code] ?? code;
  return {
    pill: code,
    label: ctx.t(`step5Validation.formatters.bloc52.${code}`, fallback),
  };
}

export function formatBonusDsnDateRange(
  start: string | undefined,
  end: string | undefined,
  ctx: FormatterContext,
): string {
  const s = (start ?? "").trim();
  const e = (end ?? "").trim();
  if (!s && !e) return "";
  const from = s ? formatSilaeDateExpression(s) : "—";
  const to = e ? formatSilaeDateExpression(e) : "—";
  return ctx.t("step5Validation.formatters.bonus.dateRange", {
    start: from,
    end: to,
    defaultValue: "Du {{start}} au {{end}}",
  });
}

export function formatBonusMemoLines(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
